import React from 'react';
import { BookOpen, GraduationCap, Globe2, Users } from 'lucide-react';

function Home({ currentLang }) {
  const content = {
    en: {
      welcome: "Welcome!",
      bio1: "Pterόessa is a passionate philologist and adult educator with years of experience teaching Greek to learners of all ages. She is dedicated to creating a supportive and motivating environment where every student can thrive. Her warm, goal-oriented approach ensures that each student reaches their individual language goals, whether that means mastering the basics or advancing to fluency.",
      bio2: "Katerina tailors lessons specifically to the needs and pace of each learner. Combining her deep linguistic expertise with a strong artistic sensibility, she excels at helping students learn efficiently, even under pressure. Her personalized methods make language learning both effective and enjoyable.",
      t1: "Professor",
      i1: "Istituto Ellenico di Cultura",
      p1: "Teaching Greek to Italian students using Italian as the medium of instruction, guiding multiple learners successfully on conquering the language. All sessions are conducted online.",
      t2: "Greek Language Teacher",
      i2: "Freelance Academic",
      p2: "Teaching Greek to students from the United States and Europe. All material used is created and provided exclusively by Pterόessa. Students may purchase material upon request.",
      t3: "K-12 Greek Language Teacher",
      i3: "Private Educator",
      p3: "Worked as a private tutor for various graduates, undergraduates, and grade-schoolers, delivering tangible educational content by leveraging state-of-the-art teaching methods and prototypes."
    },
    gr: {
      welcome: "Καλώς Ορίσατε!",
      bio1: "Η Πτερόεσσα είναι μια παθιασμένη φιλόλογος και εκπαιδεύτρια ενηλίκων με πολυετή εμπειρία στη διδασκαλία της ελληνικής γλώσσας σε μαθητές όλων των ηλικιών. Είναι αφοσιωμένη στη δημιουργία ενός υποστηρικτικού περιβάλλοντος όπου κάθε μαθητής μπορεί να ευδοκιμήσει.",
      bio2: "Η Κατερίνα προσαρμόζει τα μαθήματα ειδικά στις ανάγκες και τον ρυθμό κάθε εκπαιδευόμενου. Συνδυάζοντας τη βαθιά γλωσσολογική της τεχνογνωσία με μια ισχυρή καλλιτεχνική ευαισθησία, βοηθά τους μαθητές να μαθαίνουν αποτελεσματικά.",
      t1: "Καθηγήτρια",
      i1: "Istituto Ellenico di Cultura",
      p1: "Διδασκαλία Ελληνικών σε Ιταλούς φοιτητές με μέσο διδασκαλίας την ιταλική γλώσσα. Όλα τα μαθήματα διεξάγονται διαδικτυακά.",
      t2: "Καθηγήτρια Ελληνικών",
      i2: "Ελεύθερος Επαγγελματίας",
      p2: "Διδασκαλία Ελληνικών σε μαθητές από τις ΗΠΑ και την Ευρώπη. Όλο το υλικό δημιουργείται και παρέχεται αποκλειστικά από την ίδια.",
      t3: "Εκπαιδευτικός K-12",
      i3: "Ιδιωτική Εκπαίδευση",
      p3: "Παράδοση ιδιαίτερων μαθημάτων σε μαθητές σχολικής ηλικίας και φοιτητές, αξιοποιώντας σύγχρονες εκπαιδευτικές μεθόδους."
    },
    it: {
      welcome: "Benvenuti!",
      bio1: "Pterόessa è una filologa appassionata ed educatrice di adulti con anni di esperienza nell'insegnamento del greco a studenti di tutte le età. Si dedica a creare un ambiente di supporto in cui ogni studente possa prosperare.",
      bio2: "Katerina adatta le lezioni specificamente alle esigenze e al ritmo di ogni studente. Combinando la sua profonda competenza linguistica con una forte sensibilità artistica, aiuta gli studenti a imparare in modo efficiente.",
      t1: "Professoressa",
      i1: "Istituto Ellenico di Cultura",
      p1: "Insegnamento del greco a studenti italiani utilizzando l'italiano come lingua veicolare. Tutte le sessioni si tengono online.",
      t2: "Insegnante di Lingua Greca",
      i2: "Libera Professionista",
      p2: "Insegnamento del greco a studenti provenienti da Stati Uniti ed Europa. Tutto il materiale utilizzato è creato esclusivamente da Pterόessa.",
      t3: "Insegnante di Greco K-12",
      i3: "Tutor Privato",
      p3: "Ha lavorato come tutor privato per laureandi e studenti delle scuole dell'obbligo, offrendo contenuti educativi tangibili tramite prototipi all'avanguardia."
    }
  };

  const t = content[currentLang];

  return (
    <>
      <div className="row mb-5 justify-content-center">
        <div className="col-lg-10 col-md-12">
          <h2 className="display-6 mb-4 d-flex align-items-center gap-2">
            {t.welcome}
            <BookOpen size={26} className="text-muted" style={{ strokeWidth: 1.5 }} />
          </h2>
          <p style={{ fontSize: '17px' }}>{t.bio1}</p>
          <p style={{ fontSize: '17px' }}>{t.bio2}</p>
        </div>
      </div>

      <hr style={{ borderColor: '#eae7e2', margin: '3rem 0' }} />

      <div className="row">
        <div className="col-md-4 col-sm-12">
          <div className="experience-block">
            <h4 className="d-flex align-items-center gap-2 mb-2">
              {t.t1} <GraduationCap size={18} className="text-muted" />
            </h4>
            <div className="text-muted small text-uppercase mb-3" style={{ letterSpacing: '0.05em', fontWeight: 600 }}>{t.i1}</div>
            <p>{t.p1}</p>
          </div>
        </div>

        <div className="col-md-4 col-sm-12">
          <div className="experience-block">
            <h4 className="d-flex align-items-center gap-2 mb-2">
              {t.t2} <Globe2 size={18} className="text-muted" />
            </h4>
            <div className="text-muted small text-uppercase mb-3" style={{ letterSpacing: '0.05em', fontWeight: 600 }}>{t.i2}</div>
            <p>{t.p2}</p>
          </div>
        </div>

        <div className="col-md-4 col-sm-12">
          <div className="experience-block">
            <h4 className="d-flex align-items-center gap-2 mb-2">
              {t.t3} <Users size={18} className="text-muted" />
            </h4>
            <div className="text-muted small text-uppercase mb-3" style={{ letterSpacing: '0.05em', fontWeight: 600 }}>{t.i3}</div>
            <p>{t.p3}</p>
          </div>
        </div>
      </div>
    </>
  );
}

export default Home;